/**
 * PDF generation utility for tide tables
 * Uses jsPDF with jspdf-autotable plugin
 */
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'
import { DATE_TIME_FORMAT } from 'constants/app'
import { formatNum } from 'scenarios/calculations'

/**
 * Generate a PDF containing both tide tables
 * @param {Object} params
 * @param {Array} params.tidesAroundDeparture - Array of 15-min interval tide data around departure
 * @param {Array} params.highTides - Array of high tide data points
 * @param {number} params.boatDraft - Vessel draft in meters
 * @param {number} params.dockDepth - Dock depth in meters
 * @returns {Blob} PDF as a Blob for email attachment
 */
export function generateTidesPdf({ tidesAroundDeparture, highTides, boatDraft, dockDepth }) {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const boatDraftNum = parseFloat(boatDraft) || 0
    const dockDepthNum = parseFloat(dockDepth) || 0

    // Title
    doc.setFontSize(16)
    doc.text('Données de marées / Tide Data', pageWidth / 2, 15, { align: 'center' })

    let yPosition = 25

    // Table 1: Tides around departure
    if (tidesAroundDeparture && tidesAroundDeparture.length > 0) {
        // Calculate dégagement (clearance) for each row
        // Formula: dockDepth + tide - boatDraft (same as UI TidesTable.js)
        doc.autoTable({
            startY: yPosition,
            head: [['Date', 'Obs.', 'Prév./Prev.', 'Préd./Pred.', 'Dég.']],
            body: tidesAroundDeparture.map(t => {
                // Use wlo if available, otherwise lower of wlp and wlf-spine
                const wlo = +t.wlo || 0
                const wlp = +t.wlp || 0
                const wlf = +t['wlf-spine'] || 0
                const tide = wlo !== 0 ? wlo : wlp >= wlf ? wlf : wlp
                const clearance = dockDepthNum + tide - boatDraftNum
                return [
                    moment(t.date).format(DATE_TIME_FORMAT),
                    formatNum(t.wlo),
                    formatNum(t['wlf-spine']),
                    formatNum(t.wlp),
                    formatNum(clearance),
                ]
            }),
            styles: {
                fontSize: 9,
                cellPadding: 2,
                halign: 'center',
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 40 },
                1: { cellWidth: 22 },
                2: { cellWidth: 28 },
                3: { cellWidth: 28 },
                4: { cellWidth: 22 },
            },
        })

        yPosition = doc.lastAutoTable.finalY + 15
    }

    // Table 2: High tides
    if (highTides && highTides.length > 0) {
        doc.setFontSize(12)
        doc.text('Tableau des marées les plus hautes', 14, yPosition)
        doc.setFontSize(10)
        doc.text('High tides table', 14, yPosition + 5)

        yPosition += 10

        doc.autoTable({
            startY: yPosition,
            head: [['Date', 'Obs.', 'Prév./Prev.']],
            body: highTides.map(t => [
                moment(t.date).format(DATE_TIME_FORMAT),
                t.wlo ? formatNum(t.wlo) : '',
                t['wlf-spine'] ? formatNum(t['wlf-spine']) : '',
            ]),
            styles: {
                fontSize: 9,
                cellPadding: 2,
                halign: 'center',
            },
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
            },
            columnStyles: {
                0: { cellWidth: 45 },
                1: { cellWidth: 25 },
                2: { cellWidth: 30 },
            },
        })
    }

    // Return as Blob
    return doc.output('blob')
}

/**
 * Generate PDF and return as base64 string (for email embedding)
 * @param {Object} params - Same as generateTidesPdf
 * @returns {string} PDF as base64 data URI
 */
export function generateTidesPdfBase64(params) {
    const doc = createTidesPdfDoc(params)
    return doc.output('datauristring')
}

/**
 * Download the tides PDF directly
 * @param {Object} params - Same as generateTidesPdf
 * @param {string} filename - Name for the downloaded file
 */
export function downloadTidesPdf(params, filename = 'marees-tides.pdf') {
    const blob = generateTidesPdf(params)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

// Internal helper to create the PDF document (avoid code duplication)
function createTidesPdfDoc({ tidesAroundDeparture, highTides, boatDraft, dockDepth }) {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const boatDraftNum = parseFloat(boatDraft) || 0
    const dockDepthNum = parseFloat(dockDepth) || 0

    doc.setFontSize(16)
    doc.text('Données de marées / Tide Data', pageWidth / 2, 15, { align: 'center' })

    let yPosition = 25

    if (tidesAroundDeparture && tidesAroundDeparture.length > 0) {
        // Calculate dégagement (clearance) for each row
        // Formula: dockDepth + tide - boatDraft (same as UI TidesTable.js)
        doc.autoTable({
            startY: yPosition,
            head: [['Date', 'Obs.', 'Prév./Prev.', 'Préd./Pred.', 'Dég.']],
            body: tidesAroundDeparture.map(t => {
                // Use wlo if available, otherwise lower of wlp and wlf-spine
                const wlo = +t.wlo || 0
                const wlp = +t.wlp || 0
                const wlf = +t['wlf-spine'] || 0
                const tide = wlo !== 0 ? wlo : wlp >= wlf ? wlf : wlp
                const clearance = dockDepthNum + tide - boatDraftNum
                return [
                    moment(t.date).format(DATE_TIME_FORMAT),
                    formatNum(t.wlo),
                    formatNum(t['wlf-spine']),
                    formatNum(t.wlp),
                    formatNum(clearance),
                ]
            }),
            styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
            columnStyles: {
                0: { cellWidth: 40 },
                1: { cellWidth: 22 },
                2: { cellWidth: 28 },
                3: { cellWidth: 28 },
                4: { cellWidth: 22 },
            },
        })

        yPosition = doc.lastAutoTable.finalY + 15
    }

    if (highTides && highTides.length > 0) {
        doc.setFontSize(12)
        doc.text('Tableau des marées les plus hautes', 14, yPosition)
        doc.setFontSize(10)
        doc.text('High tides table', 14, yPosition + 5)

        yPosition += 10

        doc.autoTable({
            startY: yPosition,
            head: [['Date', 'Obs.', 'Prév./Prev.']],
            body: highTides.map(t => [
                moment(t.date).format(DATE_TIME_FORMAT),
                t.wlo ? formatNum(t.wlo) : '',
                t['wlf-spine'] ? formatNum(t['wlf-spine']) : '',
            ]),
            styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
            columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 25 }, 2: { cellWidth: 30 } },
        })
    }

    return doc
}
